/**
 * 类型安全的环境变量读取 wrapper
 * 统一从 import.meta.env 读取，避免散落的字符串引用
 *
 * 网络地址优先级：localStorage > 环境变量 > 默认值
 */

import { ref } from 'vue'
import type { Ref } from 'vue'

const LS_KEY_API_BASE = 'kiosk_config_api_base'
const LS_KEY_WS_BASE = 'kiosk_config_ws_base'

/**
 * 网络配置变更计数（localStorage 本身非响应式；UI 依赖此 tick 刷新）
 */
export const networkConfigTick: Ref<number> = ref(0)

function bumpNetworkConfigTick(): void {
  networkConfigTick.value += 1
}

/**
 * Chromium/WebView2 解析 localhost 时优先 ::1（IPv6），
 * 而本机 Gateway 通常只监听 0.0.0.0（IPv4）→ WS 会静默失败并一直 RECONNECTING。
 * 将回环主机规范为 127.0.0.1。
 */
export function normalizeLoopbackUrl(raw: string): string {
  const trimmed = raw.trim()
  if (!trimmed) return trimmed
  return trimmed.replace(/^(https?|wss?):\/\/(\[::1\]|::1|localhost)\b/i, '$1://127.0.0.1')
}

/** REST API 基础路径（优先 localStorage 覆盖） */
export function getApiBase(): string {
  let base = ''
  if (typeof localStorage !== 'undefined') {
    const stored = localStorage.getItem(LS_KEY_API_BASE)
    if (stored && stored.trim()) base = stored.trim()
  }
  if (!base) {
    base = import.meta.env.VITE_API_BASE ?? 'http://127.0.0.1:8080'
  }
  return normalizeLoopbackUrl(base)
}

/** WebSocket 基础路径（优先 localStorage 覆盖） */
export function getWsBase(): string {
  let base = ''
  if (typeof localStorage !== 'undefined') {
    const stored = localStorage.getItem(LS_KEY_WS_BASE)
    if (stored && stored.trim()) base = stored.trim()
  }
  if (!base) {
    base = import.meta.env.VITE_WS_BASE ?? 'ws://127.0.0.1:8282'
  }
  return normalizeLoopbackUrl(base)
}

/** 持久化网络配置到 localStorage */
export function setNetworkConfig(apiBase: string, wsBase: string): void {
  if (typeof localStorage !== 'undefined') {
    if (apiBase.trim()) {
      localStorage.setItem(LS_KEY_API_BASE, normalizeLoopbackUrl(apiBase.trim()))
    } else {
      localStorage.removeItem(LS_KEY_API_BASE)
    }
    if (wsBase.trim()) {
      localStorage.setItem(LS_KEY_WS_BASE, normalizeLoopbackUrl(wsBase.trim()))
    } else {
      localStorage.removeItem(LS_KEY_WS_BASE)
    }
  }
  bumpNetworkConfigTick()
}

/** 获取当前持久化的网络配置（空字符串代表使用环境变量） */
export function getStoredNetworkConfig(): { apiBase: string; wsBase: string } {
  if (typeof localStorage !== 'undefined') {
    return {
      apiBase: localStorage.getItem(LS_KEY_API_BASE) ?? '',
      wsBase: localStorage.getItem(LS_KEY_WS_BASE) ?? '',
    }
  }
  return { apiBase: '', wsBase: '' }
}

/** 浏览器环境下的机器码自动生成（用 localStorage 持久化） */
export function getBrowserMachineCode(): string {
  const key = 'kiosk_machine_code'
  if (typeof localStorage !== 'undefined') {
    const saved = localStorage.getItem(key)
    if (saved && saved.startsWith('BROWSER-')) return saved
  }

  // 生成随机指纹
  const randomStr = Math.random().toString(36).slice(2, 10).toUpperCase()
  const timestamp = Date.now().toString(36).slice(-4).toUpperCase()
  const newCode = `BROWSER-${randomStr}${timestamp}`

  if (typeof localStorage !== 'undefined') {
    localStorage.setItem(key, newCode)
  }
  return newCode
}

/** 机器编号 fallback（H5 浏览器优先 localStorage，env 仅作末位兜底） */
export function getMachineCodeFallback(): string {
  // ★ 与 resolveMachineCode / useApi 保持一致：localStorage 优先于 VITE_MACHINE_CODE_FALLBACK
  //   否则 WS 可能绑 kiosk:MAC-DEV-001，而订单走 BROWSER-xxx → 投钞推送永远 offline
  if (typeof localStorage !== 'undefined') {
    const saved = localStorage.getItem('kiosk_machine_code')
    if (saved?.trim()) return saved.trim()
  }
  const envFallback = import.meta.env.VITE_MACHINE_CODE_FALLBACK as string | undefined
  if (envFallback?.trim()) {
    return envFallback.trim()
  }
  return getBrowserMachineCode()
}

/** 应用名称 */
export function getAppName(): string {
  return import.meta.env.VITE_APP_NAME ?? 'CardShop Kiosk'
}

/** 是否为开发模式 */
export function isDev(): boolean {
  return import.meta.env.DEV === true
}

/** 是否为生产模式 */
export function isProd(): boolean {
  return import.meta.env.PROD === true
}
